
























/* eslint-disable no-case-declarations */
import { Vue } from 'vue-property-decorator';
import { IAIJob, IOrg, IBlueprint, ISurveyChoice } from '@/store/types';
import { AI_LOAD_JOBS, AI_COMPLETE_JOB, BLUEPRINT_SCHEDULE_SAVE, BLUEPRINT_GET } from '@/store/action-types';
import { SET_BLUEPRINT_STEP, UPDATE_BLUEPRINT_SURVEY, STORE_BLUEPRINT_DATA_FROM_RAW, STORE_BLUEPRINT_DATA } from '@/store/mutation-types';
import { AI_JOB_TYPES, LOAD_STATE, STEP_TYPE } from '@/store/shared';

export default Vue.extend({
  name: 'AIJobMonitor',
  components: {},
  props: {
    survey: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      interval: null,
      frequencyInSeconds: 5,
    };
  },
  computed: {
    org(): IOrg {
      return this.$store.state.org;
    },
    aiJobs(): IAIJob[] {
      if (!this.org) return [];
      if (!this.org.aiJobs) return [];
      return this.org.aiJobs;
    },
    runningJobs(): IAIJob[] {
      return this.aiJobs.filter((job: IAIJob) => job.status === 'RUNNING');
    },
    failedJobs(): IAIJob[] {
      const failed = this.aiJobs.filter((job: IAIJob) => job.status === 'FAILED');
      return failed;
    },
    readyJobs(): IAIJob[] {
      return this.aiJobs.filter((job: IAIJob) => job.status === 'READY');
    },
    blueprint(): IBlueprint {
      return this.$store.getters.currentBlueprint;
    },
    validateBlueprintSteps() {
      return this.$store.getters.validateBlueprintSteps;
    },
  },
  watch: {
    aiJobs(newValue: IAIJob[]) {
      this.adjustPolling(newValue);
    },
  },
  methods: {
    // start or stop polling based on the current state of the jobs:
    adjustPolling(jobs: IAIJob[]) {
      // console.log('>>> Adjusting polling:', jobs.length, 'jobs');
      if (this.runningJobs.length === 0) {
        // console.log('>>> AI Job Watcher: No jobs are running. Ensuring polling is stopped!');
        this.stopPolling();
      } else if (!this.$data.interval) {
        // console.log('>>> AI Job Watcher: Jobs are running, but polling is not started. Starting polling!');
        this.startPolling();
      }

      // New FAILED jobs:
      // Display error notification and mark as COMPLETED.
      const failed = jobs.filter((job: IAIJob) => job.status === 'FAILED');
      if (failed.length > 0) {
        this.$notify({
          title: 'AI Generation Failed',
          message: 'There was an error processing your request. Please try again later.',
          type: 'error',
        });
        // we showed the error notification, so we can mark these failed jobs as COMPLETED:
        failed.forEach((job: IAIJob) => {
          this.$store.dispatch(AI_COMPLETE_JOB, job._id);
        });
      }

      // New READY jobs:
      // Apply the results of the jobs to the blueprint, step or choice, and mark as COMPLETED.
      const readyJobs = jobs.filter((job: IAIJob) => job.status === 'READY');
      readyJobs.forEach((job: IAIJob) => {
        if (job.result) {
          // console.log('%c>>> AI Job Watcher: Applying job result...', 'color: green; font-weight: bold;', job);
          this.applyResult(job); // apply changes and mark job as completed
        } else {
          this.$store.dispatch(AI_COMPLETE_JOB, job._id); // mark job as completed
        }
      });
    },
    async applyResult(job: IAIJob) {
      try {
        let validatedSteps;
        switch (job.type) {
          // Replace the whole blueprint with the AI-generated (or reworked) one:
          case AI_JOB_TYPES.GEN_BLUEPRINT:
          case AI_JOB_TYPES.REWORK_BLUEPRINT:
            if (!job.result) throw new Error('No result in job!');
            if (!job.result.updatedSteps) throw new Error('No updatedSteps in job result!');
            const rawSteps = Array.isArray(job.result.updatedSteps) ? job.result.updatedSteps : job.result.updatedSteps.steps;
            if (!Array.isArray(rawSteps)) throw new Error('Invalid updatedSteps in job result!');
            validatedSteps = this.validateBlueprintSteps(rawSteps);
            this.$store.commit(STORE_BLUEPRINT_DATA, {
              id: this.blueprint._id,
              data: {
                steps: validatedSteps,
                aiGenerated: true,
                aiGenerationCompleted: new Date(),
              },
            });
            break;

          // Replace step with the AI-generated one:
          case AI_JOB_TYPES.REWORK_STEP:
            if (!job.result) {
              // console.log('>>> AI Job Watcher: No result in job!');
              break;
            }
            if (!job.result.step) {
              // console.log('>>> AI Job Watcher: No step in job result!');
              break;
            }
            // copy step.header and step.description into step.offer.header and step.offer.description
            if (job.result.step.stepType === STEP_TYPE.OFFER && job.result.step.offer) {
              job.result.step.offer.header = job.result.step.header;
              job.result.step.offer.description = job.result.step.description;
            }
            this.$store.commit(SET_BLUEPRINT_STEP, job.result.step);
            break;

          // Replace survey choice with the AI-generated one:
          case AI_JOB_TYPES.REWORK_SURVEY_CHOICE:
            if (!this.survey) {
              // console.log('>>> AI Job Watcher: Survey not loaded!');
              break;
            }
            if (!job.result?.surveyChoice) {
              // console.log('>>> AI Job Watcher: No survey choice in job result!');
              break;
            }
            const choices = this.survey.choices.map((choice: ISurveyChoice) => {
              if (choice.guid === job.surveyChoiceGuid) {
                return {
                  ...choice,
                  offer: job.result.surveyChoice,
                };
              }
              return choice;
            });
            this.$store.commit(UPDATE_BLUEPRINT_SURVEY, {
              surveyGuid: this.survey.guid,
              data: { choices },
            });
            break;

          default:
          // console.log('>>> AI Job Watcher: Unknown job type:', job.type);
        }

        // We finished applying the result, schedule a save operation:
        await this.$store.dispatch(BLUEPRINT_SCHEDULE_SAVE, { id: this.blueprint._id, delay: 0 });
      } catch (err) {
        // console.log('>>> AI Job Watcher: Error applying job result:', err);
        this.$notify({
          title: 'AI Generation Failed',
          message: 'There was an error processing your request. Please try again later.',
          type: 'error',
        });

        // if job was a blueprint generation and failed, let's disable all blueprint steps:
        // we'd only want to do this if we started the job from an empty blueprint!
        /*
        if (job.type === AI_JOB_TYPES.GEN_BLUEPRINT) {
          console.log('%c[AI] Blueprint generation failed! Disabling placeholder steps...', 'color: red;');
          const steps = this.blueprint.steps.map((step: any) => ({ ...step, enabled: false }));
          this.$store.commit(STORE_BLUEPRINT_DATA_FROM_RAW, {
            id: this.blueprint._id,
            data: {
              steps,
            },
          });
          await this.$store.dispatch(BLUEPRINT_SCHEDULE_SAVE, { id: this.blueprint._id, delay: 0 });
        }
        */
      }

      await this.$store.dispatch(AI_COMPLETE_JOB, job._id); // mark job as completed, even if there was an error
    },
    startPolling() {
      if (this.$data.interval) {
        // console.log('%c Polling already started! No need to start again!', 'color: green; font-weight: bold;');
        return;
      }
      // console.log('%c Starting polling...', 'color: green; font-weight: bold;');
      this.$data.interval = setInterval(() => {
        this.$store.dispatch(AI_LOAD_JOBS);
      }, this.frequencyInSeconds * 1000);
    },
    stopPolling() {
      // console.log('%c Stopping polling...', 'color: red; font-weight: bold;');
      clearInterval(this.$data.interval);
      this.$data.interval = null;
    },
  },
  mounted(): void {
    this.adjustPolling(this.aiJobs);
  },
  beforeDestroy(): void {
    this.stopPolling();
  },
});
